/**
 * 🐾 Fissal Stage Protocol Engine
 * Unified Stage & Production Protocols:
 * 1. DMX512: Enttec DMX USB Pro & Art-Net 4 packet encoders
 * 2. Behringer X32: Bidirectional Open Sound Control (OSC) over UDP :10023
 * 3. Tungsten Inrush Limiter: Soft-start ramp curves for 500W Altman 360Q Lekos
 * 4. Audio Metrology: Pink noise, sine sweeps, and 1kHz test tones for room calibration
 */

// -------------------------------------------------------------
// 1. DMX512 Encoders (Enttec DMX Pro & Art-Net 4)
// -------------------------------------------------------------

export const DMX_UNIVERSE_SIZE = 512;

/**
 * Encodes a 512-channel DMX universe into an Enttec DMX USB Pro packet.
 * Frame structure:
 *  0x7E: Start Delimiter
 *  0x06: Label (Send DMX Packet Request)
 *  Length LSB, Length MSB (513 = 1 start code + 512 data bytes)
 *  0x00: DMX Start Code
 *  512 bytes: Channel levels (0-255)
 *  0xE7: End Delimiter
 */
export function buildEnttecProDmxPacket(universeData: Uint8Array): Uint8Array {
  const dataLen = 513; // 1 start code + 512 channels
  const packet = new Uint8Array(4 + dataLen + 1);
  
  packet[0] = 0x7e; // Start delimiter
  packet[1] = 0x06; // Label: Output Only Send DMX
  packet[2] = dataLen & 0xff; // Length LSB (513 = 0x01)
  packet[3] = (dataLen >> 8) & 0xff; // Length MSB (513 = 0x02)
  packet[4] = 0x00; // DMX Start Code

  // Copy up to 512 channel values
  const count = Math.min(universeData.length, DMX_UNIVERSE_SIZE);
  packet.set(universeData.subarray(0, count), 5);

  packet[packet.length - 1] = 0xe7; // End delimiter
  return packet;
}

/**
 * Encodes a 512-channel DMX universe into an Art-Net 4 OpDmx packet.
 * Protocol: Art-Net v14, UDP port 6454
 */
export function buildArtNetDmxPacket(
  universe: number,
  channelData: Uint8Array,
  sequence: number = 0
): Uint8Array {
  const header = [0x41, 0x72, 0x74, 0x2d, 0x4e, 0x65, 0x74, 0x00]; // "Art-Net\0"
  const packet = new Uint8Array(18 + 512);

  // ID
  packet.set(header, 0);

  // OpCode: OpOutput / OpDmx (0x5000, Little-Endian)
  packet[8] = 0x00;
  packet[9] = 0x50;

  // Protocol Version: 14 (Big-Endian)
  packet[10] = 0x00;
  packet[11] = 0x0e;

  // Sequence (0x00 = disabled or incrementing 1-255)
  packet[12] = sequence & 0xff;

  // Physical input port
  packet[13] = 0x00;

  // SubUni (Low 8 bits of 15-bit Port-Address)
  packet[14] = universe & 0xff;

  // Net (High 7 bits of 15-bit Port-Address)
  packet[15] = (universe >> 8) & 0x7f;

  // Length: 512 bytes (Big-Endian)
  packet[16] = 0x02;
  packet[17] = 0x00;

  // Channel Data
  const copyLen = Math.min(channelData.length, 512);
  packet.set(channelData.subarray(0, copyLen), 18);

  return packet;
}

// -------------------------------------------------------------
// 2. Behringer X32 OSC Protocol Engine
// -------------------------------------------------------------

function padString(str: string): Uint8Array {
  const bytes = new TextEncoder().encode(str);
  const nullCount = 4 - (bytes.length % 4);
  const totalLen = bytes.length + nullCount;
  const padded = new Uint8Array(totalLen);
  padded.set(bytes, 0);
  return padded;
}

/**
 * Builds a binary OSC packet for a single float argument
 */
export function buildOscFloatMessage(address: string, floatVal: number): Uint8Array {
  const addrBytes = padString(address);
  const tagBytes = padString(',f');

  const valBuffer = new ArrayBuffer(4);
  const view = new DataView(valBuffer);
  view.setFloat32(0, Math.max(0, Math.min(1, floatVal)), false); // Big-endian IEEE 754
  const valBytes = new Uint8Array(valBuffer);

  const packet = new Uint8Array(addrBytes.length + tagBytes.length + 4);
  packet.set(addrBytes, 0);
  packet.set(tagBytes, addrBytes.length);
  packet.set(valBytes, addrBytes.length + tagBytes.length);
  return packet;
}

/**
 * Builds a binary OSC packet for a single integer argument
 */
export function buildOscIntMessage(address: string, intVal: number): Uint8Array {
  const addrBytes = padString(address);
  const tagBytes = padString(',i');

  const valBuffer = new ArrayBuffer(4);
  const view = new DataView(valBuffer);
  view.setInt32(0, intVal, false); // Big-endian 32-bit signed
  const valBytes = new Uint8Array(valBuffer);

  const packet = new Uint8Array(addrBytes.length + tagBytes.length + 4);
  packet.set(addrBytes, 0);
  packet.set(tagBytes, addrBytes.length);
  packet.set(valBytes, addrBytes.length + tagBytes.length);
  return packet;
}

/**
 * Builds an OSC packet with no arguments (e.g. /xremote, /xinfo)
 */
export function buildOscEmptyMessage(address: string): Uint8Array {
  const addrBytes = padString(address);
  const tagBytes = padString(',');
  const packet = new Uint8Array(addrBytes.length + tagBytes.length);
  packet.set(addrBytes, 0);
  packet.set(tagBytes, addrBytes.length);
  return packet;
}

/**
 * Standard X32 OSC Helpers
 */
export const X32 = {
  // Channel fader (1-32, 0.0 to 1.0)
  setChannelFader: (ch: number, val: number) => {
    const padCh = ch < 10 ? `0${ch}` : `${ch}`;
    return buildOscFloatMessage(`/ch/${padCh}/mix/fader`, val);
  },
  // Channel mute (1-32, true = muted, false = unmuted)
  setChannelMute: (ch: number, muted: boolean) => {
    const padCh = ch < 10 ? `0${ch}` : `${ch}`;
    // In X32 OSC, /mix/on: 1 = unmuted (ON), 0 = muted (OFF)
    return buildOscIntMessage(`/ch/${padCh}/mix/on`, muted ? 0 : 1);
  },
  // Main Stereo LR fader (0.0 to 1.0)
  setMainFader: (val: number) => buildOscFloatMessage('/main/st/mix/fader', val),
  // Main Stereo LR mute
  setMainMute: (muted: boolean) => buildOscIntMessage('/main/st/mix/on', muted ? 0 : 1),
  // DCA fader (1-8, 0.0 to 1.0)
  setDcaFader: (dca: number, val: number) => buildOscFloatMessage(`/dca/${dca}/fader`, val),
  // DCA mute (1-8, true = muted, false = unmuted)
  setDcaMute: (dca: number, muted: boolean) => buildOscIntMessage(`/dca/${dca}/on`, muted ? 0 : 1),
  // Heartbeat subscription packet (must be sent every 6-9s to keep meter & fader feedback alive)

  getXRemote: () => buildOscEmptyMessage('/xremote'),
  // X32 Info query
  getXInfo: () => buildOscEmptyMessage('/xinfo'),
};

// -------------------------------------------------------------
// 3. Electrical Load & Tungsten Protection Metrology
// -------------------------------------------------------------

export interface StageFixtureInfo {
  name: string;
  type: 'altman_360q_500w' | 'par64_500w' | 'par56_300w' | 'led_rgb_par' | 'strobe_750w' | 'fogger_1000w';
  wattsPerUnit: number;
  count: number;
  currentLevel: number; // 0-100%
  channel: number;
}

/**
 * Calculates current electrical draw in Watts and Amperes (at 120V)
 * Identifies breaker hazards (15A standard household vs 20A commercial stage circuits)
 */
export function calculateStagePower(fixtures: StageFixtureInfo[]) {
  let totalWatts = 0;
  for (const fix of fixtures) {
    const activeFactor = (fix.currentLevel / 100);
    totalWatts += fix.wattsPerUnit * fix.count * activeFactor;
  }
  const totalAmps = totalWatts / 120.0;
  return {
    totalWatts: Math.round(totalWatts),
    totalAmps: Number(totalAmps.toFixed(1)),
    exceeds15A: totalAmps > 15.0,
    exceeds20A: totalAmps > 20.0,
    percentOf20A: Math.round((totalAmps / 20.0) * 100),
  };
}

/**
 * Soft-start tungsten curve generator.
 * Tungsten filaments have a 10-15x cold inrush multiplier.
 * A 0-100% jump over <50ms can pop breakers or blow lamps.
 * This applies a logarithmic/sigmoid soft knee to any jump > 40%.
 */
export function applyTungstenSoftRamp(
  currentLevel: number,
  targetLevel: number,
  preheatEnabled: boolean = true
): number {
  const PREHEAT_FLOOR = 5; // 5% warm baseline to prevent cold filament shock
  let effectiveTarget = targetLevel;
  if (preheatEnabled && targetLevel > 0 && targetLevel < PREHEAT_FLOOR) {
    effectiveTarget = PREHEAT_FLOOR;
  }
  // Return target with soft-knee guidance
  return Math.max(0, Math.min(255, Math.round((effectiveTarget / 100) * 255)));
}

// -------------------------------------------------------------
// 4. Acoustic Metrology & Soundcheck Generator (Web Audio API)
// -------------------------------------------------------------

class SoundcheckSynthesizer {
  private ctx: AudioContext | null = null;
  private activeNode: AudioNode | null = null;
  private gainNode: GainNode | null = null;

  private initCtx() {
    if (!this.ctx) {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      this.ctx = new AudioCtx();
    }
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
  }

  /**
   * Generates continuous Pink Noise (1/f equal energy per octave)
   * The gold standard for room tuning and RTA spectrum calibration.
   */
  startPinkNoise(volume: number = 0.3) {
    this.stop();
    this.initCtx();
    if (!this.ctx) return;

    const bufferSize = this.ctx.sampleRate * 2; // 2 seconds ring buffer
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);

    // Paul Kellet's filtered pink noise algorithm
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      b0 = 0.99886 * b0 + white * 0.0555179;
      b1 = 0.99332 * b1 + white * 0.0750759;
      b2 = 0.96900 * b2 + white * 0.1538520;
      b3 = 0.86650 * b3 + white * 0.3104856;
      b4 = 0.55000 * b4 + white * 0.5329522;
      b5 = -0.7616 * b5 - white * 0.0168980;
      data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
      b6 = white * 0.115926;
    }

    const noiseSource = this.ctx.createBufferSource();
    noiseSource.buffer = buffer;
    noiseSource.loop = true;

    this.gainNode = this.ctx.createGain();
    this.gainNode.gain.setValueAtTime(volume, this.ctx.currentTime);

    noiseSource.connect(this.gainNode);
    this.gainNode.connect(this.ctx.destination);
    noiseSource.start();
    this.activeNode = noiseSource;
  }

  /**
   * Generates a pure sine wave (e.g. 1000 Hz reference tone or 100 Hz sub test)
   */
  startSineTone(freqHz: number = 1000, volume: number = 0.3) {
    this.stop();
    this.initCtx();
    if (!this.ctx) return;

    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freqHz, this.ctx.currentTime);

    this.gainNode = this.ctx.createGain();
    this.gainNode.gain.setValueAtTime(volume, this.ctx.currentTime);

    osc.connect(this.gainNode);
    this.gainNode.connect(this.ctx.destination);
    osc.start();
    this.activeNode = osc;
  }

  /**
   * Smoothly stops any active audio emission
   */
  stop() {
    if (this.gainNode && this.ctx) {
      try {
        this.gainNode.gain.linearRampToValueAtTime(0.001, this.ctx.currentTime + 0.05);
        setTimeout(() => {
          if (this.activeNode) {
            (this.activeNode as any).stop?.();
            this.activeNode.disconnect();
            this.activeNode = null;
          }
        }, 60);
      } catch {
        this.activeNode = null;
      }
    } else if (this.activeNode) {
      try {
        (this.activeNode as any).stop?.();
        this.activeNode.disconnect();
      } catch {}
      this.activeNode = null;
    }
  }

  isPlaying(): boolean {
    return this.activeNode !== null;
  }

  play1kHzTone(volume: number = 0.3) {
    this.startSineTone(1000, volume);
  }

  playPinkNoise(volume: number = 0.3) {
    this.startPinkNoise(volume);
  }
}

export const soundcheckSynth = new SoundcheckSynthesizer();

